import * as React from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '~/components/ui/dialog';
import { Button } from '@systemzero-inc/ui/primitives/button';
import { Play, Mail } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { DatePicker } from '~/components/molecules/date-picker/date-picker';
import type { ScheduledTask, RecurrenceFrequency, TaskExecutionScope } from '~/data/endpoints/scheduled-tasks';
import type { CreateScheduledTaskBody } from '~/data/endpoints/scheduled-tasks';
import { isValid } from 'date-fns';

const PROMPT_SUGGESTIONS = [
  {
    title: 'Weekly ontology quality summary',
    prompt:
      'Summarize ontology changes from the past 7 days, highlight new entities/relationships, and flag risky schema edits.',
    recurrence: 'weekly' as RecurrenceFrequency,
  },
  {
    title: 'Daily validation readiness check',
    prompt:
      'Review current ontology draft status, list unresolved validation findings, and suggest the top 3 fixes before deployment.',
    recurrence: 'daily' as RecurrenceFrequency,
  },
];

const SCOPE_OPTIONS: { value: TaskExecutionScope; label: string }[] = [
  { value: 'analyst', label: 'Analyst Copilot' },
  { value: 'ontology_network', label: 'Ontology Assistant' },
];

const RECURRENCE_OPTIONS: { value: RecurrenceFrequency; label: string }[] = [
  { value: 'once', label: 'One-time' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
  { value: 'weekly', label: 'Every week' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Every month' },
];

function safeDate(value: unknown): Date {
  if (value instanceof Date && isValid(value)) return value;
  const d = new Date(value as string | number);
  return isValid(d) ? d : new Date();
}

function toLocalISO(date: Date, timeStr: string): string {
  const [h = '0', m = '0'] = timeStr.split(':');
  const d = safeDate(date);
  const hours = Number.isNaN(parseInt(h, 10)) ? 9 : parseInt(h, 10);
  const mins = Number.isNaN(parseInt(m, 10)) ? 0 : parseInt(m, 10);
  d.setHours(hours, mins, 0, 0);
  return d.toISOString();
}

function fromScheduledAt(iso: string): { date: Date; time: string } {
  const d = safeDate(iso);
  return {
    date: d,
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
}

function defaultFutureTime(): string {
  const now = new Date();
  const mins = now.getMinutes();
  now.setMinutes(mins < 30 ? 30 : 60, 0, 0);
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function timeFromDate(d: Date | undefined): string {
  if (!d) return defaultFutureTime();
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 0 && m === 0) return defaultFutureTime();
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Optional pre-filled values for a brand-new task. Used by the command bar
 * to launch the dialog with a preset prompt + recurrence already populated
 * (e.g. "Schedule weekly ops brief"). Unlike `initialTask`, this does NOT
 * put the dialog into edit mode.
 */
export interface TaskEventDialogDraft {
  title?: string;
  description?: string;
  prompt?: string;
  recurrence?: RecurrenceFrequency;
  scope?: TaskExecutionScope;
}

export interface TaskEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTask?: ScheduledTask | null;
  initialDraft?: TaskEventDialogDraft | null;
  defaultDate?: Date;
  onSubmit: (body: CreateScheduledTaskBody, taskId?: string) => Promise<unknown>;
  onCancelTask?: (id: string) => Promise<unknown>;
  onExecuteTask?: (id: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

export function TaskEventDialog({
  open,
  onOpenChange,
  initialTask,
  initialDraft,
  defaultDate,
  onSubmit,
  onCancelTask,
  onExecuteTask,
  isSubmitting = false,
}: TaskEventDialogProps) {
  const isEdit = !!initialTask;
  const { date: initialDate, time: initialTime } = initialTask
    ? fromScheduledAt(initialTask.scheduledAt)
    : { date: safeDate(defaultDate ?? new Date()), time: timeFromDate(defaultDate) };

  const [title, setTitle] = React.useState(initialTask?.title ?? '');
  const [description, setDescription] = React.useState(initialTask?.description ?? '');
  const [prompt, setPrompt] = React.useState(initialTask?.prompt ?? '');
  const [dateValue, setDateValue] = React.useState(() => {
    try {
      return format(initialDate, 'yyyy-MM-dd');
    } catch {
      return format(new Date(), 'yyyy-MM-dd');
    }
  });
  const [time, setTime] = React.useState(initialTime);
  const [recurrence, setRecurrence] = React.useState<RecurrenceFrequency>(initialTask?.recurrence ?? 'once');
  const [scope, setScope] = React.useState<TaskExecutionScope>(initialTask?.scope ?? 'analyst');
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC', []);

  React.useEffect(() => {
    if (!open) return;
    if (initialTask) {
      setTitle(initialTask.title);
      setDescription(initialTask.description ?? '');
      setPrompt(initialTask.prompt);
      const { date, time: t } = fromScheduledAt(initialTask.scheduledAt);
      try {
        setDateValue(format(date, 'yyyy-MM-dd'));
      } catch {
        setDateValue(format(new Date(), 'yyyy-MM-dd'));
      }
      setTime(t);
      setRecurrence(initialTask.recurrence ?? 'once');
      setScope(initialTask.scope ?? 'analyst');
      setShowSuggestions(false);
    } else {
      setTitle(initialDraft?.title ?? '');
      setDescription(initialDraft?.description ?? '');
      setPrompt(initialDraft?.prompt ?? '');
      const d = safeDate(defaultDate ?? new Date());
      try {
        setDateValue(format(d, 'yyyy-MM-dd'));
      } catch {
        setDateValue(format(new Date(), 'yyyy-MM-dd'));
      }
      setTime(timeFromDate(defaultDate));
      setRecurrence(initialDraft?.recurrence ?? 'once');
      setScope(initialDraft?.scope ?? 'analyst');
      setShowSuggestions(!initialDraft?.prompt);
    }
  }, [open, initialTask, initialDraft, defaultDate]);

  const applySuggestion = (s: (typeof PROMPT_SUGGESTIONS)[number]) => {
    setTitle(s.title);
    setPrompt(s.prompt);
    setRecurrence(s.recurrence);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const date = safeDate(dateValue);
    const scheduledAt = toLocalISO(date, time);
    const body: CreateScheduledTaskBody = {
      title,
      description: description || undefined,
      prompt,
      scheduledAt,
      recurrence: recurrence !== 'once' ? recurrence : undefined,
      timezone,
      scope,
    };
    if (isEdit && initialTask) {
      await onSubmit(body, initialTask.id);
    } else {
      await onSubmit(body);
    }
  };

  const handleCancelTask = async () => {
    if (initialTask && onCancelTask) {
      await onCancelTask(initialTask.id);
      onOpenChange(false);
    }
  };

  const handleExecuteTask = async () => {
    if (initialTask && onExecuteTask) {
      await onExecuteTask(initialTask.id);
      onOpenChange(false);
    }
  };

  const canExecute =
    isEdit && initialTask && onExecuteTask && (initialTask.status === 'PENDING' || initialTask.status === 'SCHEDULED');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit scheduled task' : 'New scheduled task'}</DialogTitle>
          {!isEdit && (
            <DialogDescription>Schedule an agent to run automatically and send you the results.</DialogDescription>
          )}
        </DialogHeader>

        {/* Suggestions for new tasks */}
        {showSuggestions && !isEdit && !title && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Quick start — pick a template:</p>
            <div className="grid grid-cols-2 gap-2">
              {PROMPT_SUGGESTIONS.map((s) => (
                <button
                  key={s.title}
                  type="button"
                  className="text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => applySuggestion(s)}
                >
                  <p className="text-sm font-medium">{s.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.prompt}</p>
                  <span className="mt-1.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    {RECURRENCE_OPTIONS.find((r) => r.value === s.recurrence)?.label}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-background px-2 text-muted-foreground">or create from scratch</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Weekly revenue summary"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="task-description">
              Description <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description of this task"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="task-prompt">What should the agent do?</Label>
            <textarea
              id="task-prompt"
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Summarize last week's revenue and highlight any anomalies."
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{recurrence === 'once' ? 'Date' : 'Starting from'}</Label>
              <DatePicker value={dateValue} onDateChange={(d) => d && setDateValue(format(d, 'yyyy-MM-dd'))}>
                <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
                  {dateValue || 'Pick date'}
                </Button>
              </DatePicker>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="task-time">Time</Label>
              <Input id="task-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Repeat</Label>
            <Select value={recurrence} onValueChange={(v) => setRecurrence(v as RecurrenceFrequency)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {recurrence !== 'once' && (
              <p className="text-xs text-muted-foreground">
                The agent will run {RECURRENCE_OPTIONS.find((r) => r.value === recurrence)?.label.toLowerCase()} at{' '}
                {time || '09:00'}, starting {dateValue}.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label>Run with</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as TaskExecutionScope)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <Mail className="size-4 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              You&apos;ll receive an email with the results once the task completes, including a link to continue the
              conversation in Copilot.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {isEdit && onCancelTask && (
              <Button type="button" variant="destructive" onClick={handleCancelTask} disabled={isSubmitting}>
                Cancel task
              </Button>
            )}
            {canExecute && (
              <Button type="button" variant="outline" onClick={handleExecuteTask} disabled={isSubmitting}>
                <Play className="size-3.5 mr-1.5" />
                Run now
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isEdit ? 'Save changes' : 'Schedule task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
